import { buttonClasses, Components, circularProgressClasses, svgIconClasses, Theme } from '@mui/material';
import { green, grey, red, white } from '../../theme/palette/colors';
import { GetThemeOptions } from '../../theme/theme';
import { toPixelString } from '../../utils';

// Padding used in buttons (in pixels) for each size option
const buttonPadding = {
  small: [2, 12],
  medium: [6, 16],
};

/**
 * Component overrides (i.e. global styles, default props) applied to the MUI
 * Button to make it match the design system.
 *
 * See: https://mui.com/material-ui/api/button/#css
 */
export const getMuiButton = (_opts: GetThemeOptions): Components<Theme>['MuiButton'] => ({
  defaultProps: {
    disableElevation: true,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      ...theme.typography.button,
      borderRadius: theme.shape.controlRadius,
      // matching the color of the progress indicator to the button text color
      [`.${circularProgressClasses.root}`]: {
        color: 'inherit',
      },
    }),

    sizeSmall: {
      padding: toPixelString(buttonPadding.small),
      [`.${svgIconClasses.root}`]: {
        width: '16px',
        height: '16px',
      },
    },

    sizeMedium: {
      padding: toPixelString(buttonPadding.medium),
      [`.${svgIconClasses.root}`]: {
        width: '20px',
        height: '20px',
      },
    },

    // For outlined buttons, we need to subtract 1px for the border to ensure
    // they're the same size as the other variants
    outlinedSizeSmall: {
      padding: toPixelString(buttonPadding.small.map((p) => p - 1)),
    },
    outlinedSizeMedium: {
      padding: toPixelString(buttonPadding.medium.map((p) => p - 1)),
    },

    // This is what our "primary" variant in chrono-ui maps to
    containedPrimary: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: theme.palette.interactive.highlight.default,
          color: theme.palette.text.inversePrimary,
          [`&:hover`]: {
            backgroundColor: theme.palette.interactive.highlight.hover,
            color: theme.palette.text.inversePrimary,
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: theme.palette.interactive.neutral.disabled,
            color: theme.palette.text.disabled,
          },
        };
      }

      const isLight = theme.palette.mode === 'light';
      return {
        backgroundColor: isLight ? green[600] : green[500],
        color: isLight ? grey[50] : grey[950],
        [`&:hover`]: {
          backgroundColor: isLight ? green[800] : green[300],
          color: isLight ? grey[50] : grey[950],
        },
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: isLight ? grey[100] : grey[850],
          color: theme.palette.text.disabled,
        },
      };
    },

    // This is what our "outlined" variant in chrono-ui maps to
    outlinedSecondary: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: theme.palette.interactive.neutral.default,
          color: theme.palette.text.primary,
          borderColor: 'transparent',
          [`&:hover`]: {
            backgroundColor: theme.palette.interactive.neutral.hover,
            borderColor: 'transparent',
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: theme.palette.interactive.neutral.disabled,
            color: theme.palette.text.disabled,
            borderColor: 'transparent',
          },
        };
      }

      const isLight = theme.palette.mode === 'light';
      const color = isLight ? grey[950] : grey[50];
      const borderColor = isLight ? grey[300] : grey[600];
      const hoverBorderColor = isLight ? grey[600] : grey[200];
      const disabledColor = isLight ? grey[300] : grey[500];
      return {
        backgroundColor: isLight ? white : grey[900],
        color,
        borderColor,
        [`&:hover`]: {
          backgroundColor: isLight ? grey[50] : grey[800],
          borderColor: hoverBorderColor,
        },
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: 'transparent',
          color: disabledColor,
          borderColor,
        },
      };
    },

    // This is what our "text" variant in chrono-ui maps to
    textPrimary: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          color: theme.palette.text.primary,
          [`&:hover`]: {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.surface.hover,
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: 'unset',
            color: theme.palette.text.disabled,
          },
        };
      }

      const isLight = theme.palette.mode === 'light';
      const color = isLight ? grey[500] : grey[200];
      const hoverColor = isLight ? grey[950] : grey[50];
      const backgroundColor = isLight ? grey[100] : 'transparent';
      return {
        color,
        [`&:hover`]: {
          color: hoverColor,
          backgroundColor,
        },
        [`&.${buttonClasses.disabled}`]: {
          color: theme.palette.text.disabled,
        },
      };
    },

    // This is what our "danger primary" variant in chrono-ui maps to
    containedError: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: theme.palette.interactive.destructive.default,
          color: theme.palette.text.contrast,
          [`&:hover`]: {
            backgroundColor: theme.palette.interactive.destructive.hover,
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: theme.palette.interactive.destructive.disabled,
            color: theme.palette.text.disabled,
          },
        };
      }

      const isLight = theme.palette.mode === 'light';
      const backgroundColor = isLight ? red[600] : red[500];
      const color = isLight ? grey[50] : grey[950];
      const hoverBackgroundColor = isLight ? red[700] : red[100];
      return {
        backgroundColor,
        color,
        [`&:hover`]: {
          backgroundColor: hoverBackgroundColor,
        },
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: isLight ? grey[100] : grey[850],
          color: theme.palette.text.disabled,
        },
      };
    },

    // This is what our "danger text" variant in chrono-ui maps to
    textError: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          color: theme.palette.text.destructive,
          [`&:hover`]: {
            backgroundColor: theme.palette.interactive.destructive.subtle.default,
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: 'unset',
            color: theme.palette.text.disabled,
          },
        };
      }

      const isLight = theme.palette.mode === 'light';
      const color = isLight ? red[600] : red[500];
      const hoverBackgroundColor = isLight ? red[100] : red[800];
      const disabledColor = isLight ? grey[300] : grey[500];
      return {
        color,
        [`&:hover`]: {
          backgroundColor: hoverBackgroundColor,
        },
        [`&.${buttonClasses.disabled}`]: {
          color: disabledColor,
        },
      };
    },
  },
});
